import {
    ApiQueryEndpoints,
    ApiQuery,
    QueryResponse,
    SortDirections,
    FilterMetadata,
    FilterOperators,
    FilterTypes,
    Filter,
} from "~/modules/filters/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
    ApiSearchLayout,
} from "~/modules/filters/components/ApiSearchLayout";
import { ExportTableNames } from "~/modules/downloads/types";
import { SearchControlModes } from "~/modules/filters/components/FilterControls/types";
import { ColumnMetadata, RowSizes } from "~/components/DataGrid/types";
import { DataGridRow } from "~/components/DataGrid/DataGridRow";
import { DataGrid } from "~/components/DataGrid";
import { Token, TokenColumns } from "~/modules/tokens/types";
import {
    ASSET_NAME_AND_TICKER_COLUMN,
    PROTOCOL_COLUMN,
    ASSET_CLASS_COLUMN,
    TOKEN_BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    TOKEN_HOLDERS_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    TOKEN_CONTRACT_ADDRESS_COLUMN,
    NETWORK_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
} from "~/modules/tokens/components/TokenTable/columns";
import { Network } from "../../types";
import { DEFAULT_TOKEN_LIST_QUERY } from "~/modules/tokens/consts";

// // // //

const TOKEN_TABLE_COLUMNS: ColumnMetadata<Token, TokenColumns>[] = [
    ASSET_NAME_AND_TICKER_COLUMN,
    TOKEN_BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    TOKEN_HOLDERS_COLUMN,
    ASSET_CLASS_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    PROTOCOL_COLUMN,
    {
        ...INCORPORATION_COUNTRY_COLUMN,
        loginRequired: false,
    },
    {
        ...LICENSE_OR_REGULATORY_FRAMEWORK,
        loginRequired: false,
    },
    TOKEN_CONTRACT_ADDRESS_COLUMN,
];

const CHILD_NETWORK_TOKEN_TABLE_COLUMNS: ColumnMetadata<Token, TokenColumns>[] =
    [
        ASSET_NAME_AND_TICKER_COLUMN,
        TOKEN_BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
        TOKEN_HOLDERS_COLUMN,
        ASSET_CLASS_COLUMN,
        TOKENIZATION_TYPE_COLUMN,
        PROTOCOL_COLUMN,
        {
            ...NETWORK_COLUMN,
            label: "Avalanche L1",
        },
        {
            ...INCORPORATION_COUNTRY_COLUMN,
            loginRequired: false,
        },
        {
            ...LICENSE_OR_REGULATORY_FRAMEWORK,
            loginRequired: false,
        },
        TOKEN_CONTRACT_ADDRESS_COLUMN,
    ];

// // // //

function getTokensQuery(network: Network, childNetworks: Network[]): ApiQuery {
    const filter: Filter =
        childNetworks.length > 0
            ? {
                  operator: FilterOperators.And,
                  filters: [
                      {
                          operator: FilterOperators.Or,
                          filters: childNetworks.map((childNetwork) => ({
                              field: TokenColumns.network_slug,
                              operator: FilterOperators.Equals,
                              value: childNetwork.slug,
                          })),
                      },
                  ],
              }
            : {
                  operator: FilterOperators.And,
                  filters: [
                      {
                          operator: FilterOperators.Equals,
                          field: TokenColumns.network_slug,
                          value: network.slug,
                      },
                  ],
              };

    return {
        ...DEFAULT_TOKEN_LIST_QUERY,
        filter,
        sort: {
            field: TokenColumns.bridged_token_value_dollar,
            direction: SortDirections.desc,
        },
    };
}

export function NetworkTokens(props: {
    network: Network;
    childNetworks: Network[];
    filterMetadata: FilterMetadata[];
}) {
    const { network, childNetworks } = props;

    const TOKENS_QUERY = getTokensQuery(network, childNetworks);

    const hasChildNetworks = childNetworks.length > 0;

    const initialState: QueryResponse<Token> = {
        results: [],
        sort: {
            field: TokenColumns.bridged_token_value_dollar,
            direction: SortDirections.desc,
        },
        pagination: {
            page: 1,
            perPage: 10,
            resultCount: 10,
            pageCount: 1,
        },
    };

    // NOTE - only show inline filters for asset_class_slug and protocol_slug
    const filterMetadata = props.filterMetadata
        .map((fm) => {
            return {
                ...fm,
                defaultValue: null,
            };
        })
        .filter((fm) => {
            const includedFilters = [
                TokenColumns.asset_class_slug,
                TokenColumns.protocol_slug,
            ];

            // Include network filter if the network has child networks
            if (hasChildNetworks) {
                includedFilters.push(TokenColumns.network_slug);
            }

            return includedFilters.includes(fm.field as TokenColumns);
        })
        // Exclude dropdowns with only one option
        .filter(
            (fm) =>
                fm.type === FilterTypes.StringSingleSelect &&
                (fm.selectOptions?.length ?? 0) > 1,
        );

    // Switch between table columns based on whether the network has child networks
    const tableColumns = hasChildNetworks
        ? CHILD_NETWORK_TOKEN_TABLE_COLUMNS
        : TOKEN_TABLE_COLUMNS;

    return (
        <div className="mt-6">
            <ApiQueryStateManager
                defaultFilters={TOKENS_QUERY}
                modifyApiQuery={(apiQuery) => {
                    // TODO - handle child networks here
                    if (!apiQuery.filter) {
                        if (hasChildNetworks) {
                            apiQuery.filter = {
                                operator: FilterOperators.And,
                                filters: [
                                    {
                                        operator: FilterOperators.Or,
                                        filters: childNetworks.map(
                                            (childNetwork) => ({
                                                field: TokenColumns.network_slug,
                                                operator:
                                                    FilterOperators.Equals,
                                                value: childNetwork.slug,
                                            }),
                                        ),
                                    },
                                ],
                            };
                        } else {
                            apiQuery.filter = {
                                operator: FilterOperators.And,
                                filters: [
                                    {
                                        field: TokenColumns.network_slug,
                                        operator: FilterOperators.Equals,
                                        value: network.slug,
                                    },
                                ],
                            };
                        }
                    }

                    return apiQuery;
                }}
            >
                {({ appliedQuery, internalQuery, resetQuery }) => (
                    <ApiQueryFetcher
                        appliedQuery={appliedQuery.query}
                        internalQuery={internalQuery.query}
                        endpoint={ApiQueryEndpoints.TokenV4}
                        initialState={initialState}
                    >
                        {(state) => {
                            return (
                                <ApiSearchLayout
                                    title="Assets"
                                    filterMetadata={filterMetadata}
                                    onResetQuery={() => {
                                        resetQuery();
                                    }}
                                    appliedQuery={appliedQuery}
                                    internalQuery={internalQuery}
                                    queryFetcherState={state}
                                    controls={{
                                        // NOTE - disable export + search + apiTools for now
                                        exportData: {
                                            enabled: true,
                                            exportTableName:
                                                ExportTableNames.tokens_v4,
                                        },
                                        search: {
                                            enableBasic: true,
                                            enablePro: false,
                                            mode: SearchControlModes.Pro,
                                        },
                                        apiTools: {
                                            enabled: false, // TODO - enable this?
                                            endpoint: ApiQueryEndpoints.TokenV4,
                                        },
                                        queryExamples: {
                                            items: [],
                                            onSelectItem: (selectedExample) => {
                                                internalQuery.update(
                                                    selectedExample.query,
                                                );
                                            },
                                        },
                                        sharing: {
                                            enabled: false,
                                            url: appliedQuery.url,
                                        },
                                    }}
                                >
                                    <DataGrid
                                        query={appliedQuery.query}
                                        columns={tableColumns}
                                        rows={
                                            state.appliedQueryReq.data.results
                                        }
                                        rowKey="id"
                                        bordered={true}
                                        size={RowSizes.small}
                                        loading={state.appliedQueryReq.loading}
                                        fixedHeader={true}
                                        analyticsTableName="network-individual-tokens"
                                        paginationState={
                                            state.appliedQueryReq.data
                                                .pagination
                                        }
                                        onChangeQuery={appliedQuery.update}
                                        enableItemsPerPageSelect={false}
                                        enableScrollButton={false}
                                        flush={false}
                                        showHeader
                                    >
                                        {({ row, rowIndex, scrollLeft }) => (
                                            <DataGridRow<Token, TokenColumns>
                                                row={row}
                                                key={row.id}
                                                columns={tableColumns}
                                                size={RowSizes.small}
                                                bordered
                                                scrollLeft={scrollLeft}
                                                striped={
                                                    true && rowIndex % 2 === 1
                                                }
                                            />
                                        )}
                                    </DataGrid>
                                </ApiSearchLayout>
                            );
                        }}
                    </ApiQueryFetcher>
                )}
            </ApiQueryStateManager>
        </div>
    );
}
